import Image from 'next/image';
import { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';

export interface BannerPhase {
  phase: number;
  start: string;
  countdownDate?: string | null;
  logo: 'xumoplay' | 'peacock';
  countdownTitle?: string | null;
  showTimer?: boolean;
  leftImage: string;
  leftImageLogo?: string;
  rightImage: string;
  rightImageMobile?: string;
  rightImageLogo?: string;
  rightBgColor?: string;
  title: string;
  altMobileTitle?: string;
  description: string;
  ctaButton?: string;
  ctaLink?: string;
  hideSwirl?: boolean;
}

const phaseData: BannerPhase[] = [
  {
    phase: 1,
    start: '2026-01-01T00:00:00.000Z',
    countdownDate: '2026-08-06T04:00:00.000Z',
    showTimer: true,
    logo: 'xumoplay',
    countdownTitle: 'RISING season 2 coming soon',
    leftImage: '/static/images/refresh/2026/nascar/driver1a.png',
    leftImageLogo: '/static/images/refresh/2026/nascar/nascar-rising-lockupl-logo-only.png',
    rightImage: '/static/images/refresh/2026/nascar/ChevronsDesktopNew.png',
    rightImageMobile: '/static/images/refresh/2026/nascar/ChevronsMobileNew.png',
    rightImageLogo: '/static/images/refresh/2026/nascar/xp-app.png',
    title: 'RAW TALENT. REAL ACCESS.',
    altMobileTitle: 'RAW<br/>TALENT.<br/>REAL<br/>ACCESS.',
    description: 'Get ready for the season 2 premiere streaming free exclusively on Xumo Play this August.',
    ctaButton: 'Catch up on season 1 now',
    ctaLink: 'https://play.xumo.com/tv-shows/rising/XM0TO5J03S6JQ1?utm_source=xumocom&utm_medium=homepage&utm_campaign=rising_s1',
    hideSwirl: true,
  },
  {
    phase: 2,
    start: '2026-08-06T04:01:00.000Z',
    countdownDate: '2026-08-06T04:00:00.000Z',
    showTimer: true,
    logo: 'xumoplay',
    countdownTitle: 'Stream season 2 free now',
    leftImage: '/static/images/refresh/2026/nascar/driver2b.png',
    leftImageLogo: '/static/images/refresh/2026/nascar/nascar-rising-lockupl-logo-only.png',
    rightImage: '/static/images/refresh/2026/nascar/ChevronsDesktopNew.png',
    rightImageMobile: '/static/images/refresh/2026/nascar/ChevronsMobileNew.png',
    rightImageLogo: '/static/images/refresh/2026/nascar/xp-app.png',
    title: 'MEET THE NEXT GENERATION.',
    altMobileTitle: 'MEET <br/>THE NEXT GENERATION.',
    description: 'Witness greatness in the making with the NASCAR® RISING season 2 premiere exclusively on Xumo Play. ',
    ctaButton: 'Stream free now',
    ctaLink: 'https://play.xumo.com/networks/nascar-channel/99951413/XM0WX6C0YMZY07/537999?utm_source=xumocom&utm_medium=takeover&utm_campaign=rising_s2',
    hideSwirl: true,
  },
  {
    phase: 3,
    start: '2026-10-06T04:01:00.000Z',
    countdownDate: '2026-10-06T04:00:00.000Z',
    showTimer: false,
    logo: 'xumoplay',
    countdownTitle: 'Stream season 2 free now',
    leftImage: '/static/images/refresh/2026/nascar/driver2b.png',
    leftImageLogo: '/static/images/refresh/2026/nascar/nascar-rising-lockupl-logo-only.png',
    rightImage: '/static/images/refresh/2026/nascar/ChevronsDesktopNew.png',
    rightImageMobile: '/static/images/refresh/2026/nascar/ChevronsMobileNew.png',
    rightImageLogo: '/static/images/refresh/2026/nascar/xp-app.png',
    title: 'MEET THE NEXT GENERATION.',
    altMobileTitle: 'MEET <br/>THE NEXT GENERATION.',
    description: 'Witness greatness in the making with the NASCAR® RISING season 2 premiere exclusively on Xumo Play. ',
    ctaButton: 'Stream free now',
    ctaLink: 'https://play.xumo.com/networks/nascar-channel/99951413/XM0Y1TSRG3KN3B/557609?utm_source=xumocom&utm_medium=homepage&utm_campaign=rising_s2e3',
    hideSwirl: true,
  },
];

const LOGO_SRC: Record<BannerPhase['logo'], string> = {
  xumoplay: '/static/images/refresh/2026/fifa/xumoplay.svg',
  peacock: '/static/images/refresh/2026/fifa/peacock.png',
};

function resolvePhaseWithOverride(phases: BannerPhase[]): BannerPhase | null {
  if (phases.length === 0) return null;
  const params = new URLSearchParams(window.location.search);
  const testPhase = params.get('countdown_phase');
  if (testPhase !== null) {
    const match = phases.find(p => String(p.phase) === testPhase);
    if (match) return match;
  }
  return getActivePhase(phases);
}

function getActivePhase(phases: BannerPhase[]): BannerPhase {
  const sorted = [...phases].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  const now = Date.now();
  console.log('[CountdownBannerNoVideo] Current user time:', new Date(now).toISOString());
  let active = sorted[0];
  for (const p of sorted) {
    const phaseStart = new Date(p.start).getTime();
    console.log(`[CountdownBannerNoVideo] Phase ${p.phase} start: ${p.start} | active: ${phaseStart <= now}`);
    if (phaseStart <= now) active = p;
  }
  console.log(`[CountdownBannerNoVideo] Selected phase: ${active.phase}`);
  return active;
}

// Counter

const CountdownSection = ({ label, value }: { label: string; value: any }) => {
  return (
    <p className="flex flex-row items-center justify-center gap-1 text-3xl leading-none xs:gap-2 lg:text-4xl">
      <span className="tabular-nums">{value}</span>
      <span className="text-2xl font-light lg:text-3xl">{label}</span>
    </p>
  );
};

const Countdown = ({ date }: { date: string }) => {
  const TARGET = new Date(date);

  function getTimeLeft() {
    const diff = Math.max(0, TARGET.getTime() - Date.now());
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor(diff / 3600000) % 24,
      minutes: Math.floor(diff / 60000) % 60,
      seconds: Math.floor(diff / 1000) % 60,
    };
  }

  const [time, setTime] = useState<ReturnType<typeof getTimeLeft> | null>(null);

  useEffect(() => {
    setTime(getTimeLeft());
    const id = setInterval(() => setTime(getTimeLeft()), 1000);
    return () => clearInterval(id);
  }, []);

  const pad = (n: number) => String(n).padStart(2, '0');

  if (!time) return null;

  return (
    <div className="flex flex-row items-center justify-center gap-3 xs:gap-5">
      <CountdownSection label="D" value={pad(time.days)} />
      <CountdownSection label="H" value={pad(time.hours)} />
      <CountdownSection label="M" value={pad(time.minutes)} />
      <CountdownSection label="S" value={pad(time.seconds)} />
    </div>
  );
};
// Banner

export const CountdownBannerNoVideo = ({ phases = phaseData }: { phases?: BannerPhase[] }) => {
  const [activePhase, setActivePhase] = useState<BannerPhase | null>(null);

  useEffect(() => {
    setActivePhase(resolvePhaseWithOverride(phases));
  }, [phases]);

  // Re-evaluate phase at every whole-minute boundary so transitions
  // happen automatically without a page refresh.
  // Skip when a manual ?countdown_phase override is active.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).has('countdown_phase')) return;

    let intervalId: ReturnType<typeof setInterval>;
    const msUntilNextMinute = 60000 - (Date.now() % 60000);

    const timeoutId = setTimeout(() => {
      setActivePhase(resolvePhaseWithOverride(phases));
      intervalId = setInterval(() => {
        setActivePhase(resolvePhaseWithOverride(phases));
      }, 60000);
    }, msUntilNextMinute);

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [phases]);

  if (!activePhase) return null;

  const titleWidth = activePhase.showTimer ? 'lg:max-w-[280px]' : 'lg:max-w-[700px]';
  return (
    <div className="mx-auto mb-3 mt-6 flex h-full w-full max-w-[1440px] flex-col items-center justify-center text-white">
      {/* Countdown */}
      <div className="flex h-max w-full flex-row items-center gap-6 overflow-hidden border-b border-white bg-gradient-to-b from-[#190A19] to-[#64325A] px-5 py-7 xs:px-9 lg:gap-10 lg:py-4 xl:gap-20">
        <div
          className={`flex h-max flex-col items-start gap-6 lg:flex-row lg:items-center lg:gap-10 lg:px-12 lg:py-4 xl:gap-20  ${
            activePhase.showTimer && activePhase.countdownDate ? 'justify-start' : 'w-full'
          }`}
        >
          <Image
            src={LOGO_SRC[activePhase.logo]}
            alt={activePhase.logo === 'xumoplay' ? 'Xumo Play' : 'Peacock'}
            width={300 / 1.25}
            height={33 / 1.25}
            priority
          />
          <p
            className={`  ${
              activePhase.showTimer && activePhase.countdownDate ? 'w-[280px] md:text-center lg:text-2xl' : 'w-full lg:text-right lg:text-3xl'
            } text-lg leading-none md:text-xl ${titleWidth}`}
          >
            {activePhase.countdownTitle}
          </p>
          {/* counter */}
          {activePhase.showTimer && activePhase.countdownDate && <Countdown date={activePhase.countdownDate} />}
        </div>
        {!activePhase.hideSwirl && (
          <div className="flex items-center justify-center lg:h-10 [@media(max-width:550px)]:hidden">
            <Image
              src="/static/images/refresh/2026/fifa/swirl-fifa.png"
              alt="Xumo Swirl"
              width={1048 / 8}
              height={723 / 8}
              priority
              className="translate-x-8 translate-y-[-40px] scale-[2.5] lg:translate-y-[-10px] lg:scale-[1.7]"
            />
          </div>
        )}
      </div>
      {/* Split banner */}
      <div className="flex w-full flex-col overflow-hidden lg:max-h-[448px] lg:flex-row">
        {/* Left: image + optional logo */}
        <div className="relative flex min-h-[320px] w-full items-end justify-center overflow-hidden lg:min-h-[450px] lg:w-2/5">
          <Image src={activePhase.leftImage} alt="" fill priority className="object-cover object-top" />
          {activePhase.leftImageLogo && (
            <Image
              src={activePhase.leftImageLogo}
              alt=""
              width={370}
              height={120}
              priority
              className="relative z-10 m-6 h-auto w-[160px] sm:w-[200px] lg:m-8 lg:w-[350px]"
            />
          )}
        </div>
        {/* Right: content over optional graphic background */}
        <div
          className="relative flex w-full flex-col items-start justify-center gap-5 overflow-hidden bg-black px-8 py-10 lg:w-3/5 lg:items-start lg:px-14 lg:py-12"
          style={activePhase.rightBgColor ? { backgroundColor: activePhase.rightBgColor } : undefined}
        >
          {activePhase.rightImageMobile ? (
            <>
              <Image src={activePhase.rightImageMobile} alt="" fill priority className="object-cover lg:hidden" />
              <Image src={activePhase.rightImage} alt="" fill priority className="hidden object-cover lg:block" />
            </>
          ) : (
            <Image src={activePhase.rightImage} alt="" fill priority className="object-cover" />
          )}
          {activePhase.rightImageLogo && (
            <Image
              src={activePhase.rightImageLogo}
              alt=""
              width={120}
              height={70}
              priority
              className="relative z-10 h-auto w-[90px] self-end lg:absolute lg:bottom-8 lg:right-8 lg:self-auto"
            />
          )}
          <h2 className="relative z-10 max-w-[570px] text-left text-[55px] font-extrabold uppercase leading-[0.9] tracking-tight [font-stretch:condensed] lg:text-6xl">
            <span className="lg:hidden" dangerouslySetInnerHTML={{ __html: activePhase.altMobileTitle ?? activePhase.title }} />
            <span className="hidden lg:inline" dangerouslySetInnerHTML={{ __html: activePhase.title }} />
          </h2>
          <p className="relative z-10 max-w-[420px] text-left text-base font-medium leading-snug [text-wrap:balance]">{activePhase.description}</p>
          {activePhase.ctaButton && activePhase.ctaLink && (
            <a
              href={activePhase.ctaLink}
              target="_self"
              className="relative z-10 rounded-sm border border-white bg-white px-8 py-4 text-center text-sm font-bold text-black no-underline transition-colors hover:bg-black hover:text-white sm:text-base"
            >
              {activePhase.ctaButton}
            </a>
          )}
        </div>
      </div>
    </div>
  );
};
